import axios from 'axios';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Debug: Verificar se a API Key está sendo carregada
console.log('Geocoding Service: API Key carregada:', GOOGLE_MAPS_API_KEY ? 'SIM (primeiros 10 chars: ' + GOOGLE_MAPS_API_KEY.substring(0, 10) + '...)' : 'NÃO');
console.log('Geocoding Service: Todas as variáveis de ambiente:', Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')));

export interface GeocodingAddress {
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
  coordinates: {
    lat: number;
    lng: number;
  };
}

export interface GeocodingResult {
  formatted_address: string;
  address_components: Array<{
    long_name: string;
    short_name: string;
    types: string[];
  }>;
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
}

const geocodingService = {
  /**
   * Buscar endereço completo e coordenadas a partir do CEP
   */
  getAddressByZipCode: async (zipCode: string): Promise<GeocodingAddress | null> => {
    try {
      // Remover caracteres não numéricos do CEP
      const cleanZipCode = zipCode.replace(/\D/g, '');
      
      if (cleanZipCode.length !== 8) {
        throw new Error('CEP inválido');
      }

      console.log('Geocoding: Buscando endereço para CEP:', cleanZipCode);

      // Fazer requisição para Google Maps Geocoding API
      const response = await axios.get(
        `https://maps.googleapis.com/maps/api/geocode/json`,
        {
          params: {
            address: cleanZipCode + ', Brazil',
            key: GOOGLE_MAPS_API_KEY
          }
        }
      );

      console.log('Geocoding: Resposta da API:', response.data);

      if (response.data.status !== 'OK' || !response.data.results || response.data.results.length === 0) {
        console.warn('Geocoding: Nenhum resultado encontrado para o CEP:', cleanZipCode);
        return null;
      }

      const result: GeocodingResult = response.data.results[0];
      
      // Extrair componentes do endereço
      const addressComponents = result.address_components;
      
      const getComponent = (types: string[]): string => {
        const component = addressComponents.find(comp => 
          types.some(type => comp.types.includes(type))
        );
        return component?.long_name || '';
      };

      const getShortComponent = (types: string[]): string => {
        const component = addressComponents.find(comp => 
          types.some(type => comp.types.includes(type))
        );
        return component?.short_name || '';
      };

      // Montar objeto de endereço
      const address: GeocodingAddress = {
        street: getComponent(['route', 'street_address']),
        number: getComponent(['street_number']),
        neighborhood: getComponent(['sublocality', 'sublocality_level_1', 'neighborhood']),
        city: getComponent(['locality', 'administrative_area_level_2']),
        state: getShortComponent(['administrative_area_level_1']),
        zipCode: cleanZipCode,
        coordinates: {
          lat: result.geometry.location.lat,
          lng: result.geometry.location.lng
        }
      };

      console.log('Geocoding: Endereço processado:', address);
      return address;
    } catch (error: any) {
      console.error('Geocoding: Erro ao buscar endereço:', error);
      throw error;
    }
  },

  /**
   * Buscar coordenadas a partir de um endereço completo
   */
  getCoordinatesByAddress: async (address: string): Promise<{ lat: number; lng: number } | null> => {
    try {
      console.log('Geocoding: Buscando coordenadas para endereço:', address);

      const response = await axios.get(
        `https://maps.googleapis.com/maps/api/geocode/json`,
        {
          params: {
            address: address + ', Brazil',
            key: GOOGLE_MAPS_API_KEY
          }
        }
      );

      console.log('Geocoding: Resposta da API:', response.data);

      if (response.data.status !== 'OK' || !response.data.results || response.data.results.length === 0) {
        console.warn('Geocoding: Nenhum resultado encontrado para o endereço:', address);
        return null;
      }

      const result: GeocodingResult = response.data.results[0];
      
      const coordinates = {
        lat: result.geometry.location.lat,
        lng: result.geometry.location.lng
      };

      console.log('Geocoding: Coordenadas encontradas:', coordinates);
      return coordinates;
    } catch (error: any) {
      console.error('Geocoding: Erro ao buscar coordenadas:', error);
      throw error;
    }
  }
};

export default geocodingService;
